import React, { useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Form, Badge } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { numericKeydownHandler } from '../../../utilities/common';
import { useDispatch, useSelector } from 'react-redux';
import { LoadingState } from '../../../utilities/constants';
import { fetchEntityTree, selectRootEntity } from '../../entity/entitySlice';
import { formatNumber } from '../../reports/reportHelper';
import { formatCo2eFactor } from '../scenarioConfig';
import { recursiveGetIds, recursiveMap } from '../../../utilities/treeHelpers';
import TreeViewModal from '../../../components/TreeViewModal';
import { InfoTile, SiteEntityTile } from '../components/Common';
import { getEnabledEntitiesIdsBySolarApi } from '../scenarioApi';

const RooftopSolarForm = ({ baseline, mode, loadModelBaseline, setResult }) => {
  const { t } = useTranslation();
  const {
    register,
    formState: { errors },
  } = useFormContext();

  const dispatch = useDispatch();
  const entityStatus = useSelector((state) => state.entities.status);
  const rootEntity = useSelector(selectRootEntity);
  const orgId = useSelector((state) => state.users.user.usrOrgId);
  const entityTree = useSelector((state) => state.entities.tree);
  const [showEntitySelector, setShowEntitySelector] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [enabledEntityIds, setEnabledEntityIds] = useState([]);

  useEffect(() => {
    const loadEnabledEntities = async () => {
      try {
        const ids = await getEnabledEntitiesIdsBySolarApi(orgId);
        setEnabledEntityIds(ids);
      } catch (err) {
        console.error('Failed to load enabled solar entities', err);
      }
    };

    loadEnabledEntities();
  }, [orgId]);

  useEffect(() => {
    if (entityStatus === LoadingState.idle) {
      dispatch(fetchEntityTree({ orgId }));
    }
  }, [orgId, entityStatus, dispatch]);
  const energy = baseline?.data?.energy;

  const transformedTree = useMemo(() => {
    if (!entityTree || !rootEntity) return {};

    return recursiveMap(entityTree, (entity) => {
      const isRoot = entity.divId === rootEntity.divId;
      const isEnabled =
        isRoot || enabledEntityIds.includes(entity.divId);

      const onSelect = () => {
        setSelectedEntity(entity);
        setShowEntitySelector(false);
        setResult(null);
        if (isRoot) {
          loadModelBaseline({});
          return;
        }
        loadModelBaseline({
          entityIds: recursiveGetIds(entity, 'divId'),
        });
      };

      return {
        value: entity.divId,
        labelRaw: entity.divName,
        label: (
          <span
            style={{
              opacity: isEnabled ? 1 : 0.4,
              cursor: isEnabled ? 'pointer' : 'not-allowed',
            }}
            onClick={isEnabled ? onSelect : undefined}
          >
            {entity.divName}
          </span>
        ),
        disabled: !isEnabled,
        isActive:
          selectedEntity?.divId === entity.divId ||
          (!selectedEntity && isRoot),
        valueId: entity.divAssignedId,
        raw: entity,
      };
    });
  }, [entityTree, enabledEntityIds, rootEntity, selectedEntity, loadModelBaseline, setResult]);
  transformedTree.isRoot = true;

  return (
    <>
      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="font-weight-bold">
              {t('CarbonView Auto-Populated Data')}
            </div>
            <Badge
              style={{
                backgroundColor: '#F3E8FF',
                color: '#7C3AED',
              }}
            >
              {t('Read-Only')}
            </Badge>
          </div>

          <div className="text-muted small mb-4">
            {t('Site and grid data from your CarbonView account')}
          </div>

          <Row>
            <Col xs={12} md={4}>
              <SiteEntityTile
                label={t('Site Name')}
                entityName={selectedEntity?.divName || rootEntity?.divName || '...'}
                onSwitch={() => setShowEntitySelector(true)}
              />
            </Col>

            <Col xs={12} md={4}>
              <InfoTile
                label={t('Annual Consumption')}
                value={energy ? `${formatNumber(energy.consumption)}/${energy.unit ?? 'kWh'}` : '...'}
              />
            </Col>

            <Col xs={12} md={4}>
              <InfoTile
                label={t('Grid Emission Factor')}
                value={energy ? `${formatCo2eFactor(energy.emissionFactor)} tCO₂e/${energy.unit ?? 'kWh'}` : '...'}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-1">
            <div className="font-weight-bold">
              {t('User Parameters')}
            </div>
            <Badge variant="success">
              {t('Required')}
            </Badge>
          </div>

          <div className="text-muted small mb-4">
            {t('Enter system specifications and financial assumptions')}
          </div>

          {/* Row 1 */}
          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  {t('Solar Panel Capacity (kW)')} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  onKeyDown={numericKeydownHandler}
                  {...register('capacity')}
                  isInvalid={!!errors.capacity}
                  disabled={mode === 'view'}
                />
                <Form.Text className="text-muted">
                  {t('Total solar panel system capacity')}
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.capacity?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  {t('CAPEX (£)')} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  onKeyDown={numericKeydownHandler}
                  {...register('capex')}
                  disabled={mode === 'view'}
                  isInvalid={!!errors.capex}
                />
                <Form.Text className="text-muted">
                  {t('Total installation cost')}
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.capex?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  {t('Tariff Rate (£/kWh)')} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  onKeyDown={numericKeydownHandler}
                  placeholder={t('e.g., 0.18')}
                  {...register('tariff')}
                  disabled={mode === 'view'}
                  isInvalid={!!errors.tariff}
                />
                <Form.Text className="text-muted">
                  {t('Electricity cost per kWh')}
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.tariff?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          {/* Row 2 */}
          <Row className="mt-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  {t('Panel Efficiency (%)')} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  onKeyDown={numericKeydownHandler}
                  placeholder={t('e.g., 20')}
                  {...register('panelEfficiency')}
                  disabled={mode === 'view'}
                  isInvalid={!!errors.panelEfficiency}
                />
                <Form.Text className="text-muted">
                  {t('Solar panel conversion efficiency')}
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.panelEfficiency?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  {t('Inverter Efficiency (%)')} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  onKeyDown={numericKeydownHandler}
                  placeholder={t('e.g., 96')}
                  {...register('inverterEfficiency')}
                  isInvalid={!!errors.inverterEfficiency}
                />
                <Form.Text className="text-muted">
                  {t('DC to AC conversion efficiency')}
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.inverterEfficiency?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  {t('Annual Generation (kWh)')} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  onKeyDown={numericKeydownHandler}
                  placeholder={t('e.g., 96')}
                  disabled={mode === 'view'}
                  {...register('annualGeneration')}
                  isInvalid={!!errors.annualGeneration}
                />
                <Form.Text className="text-muted">
                  {t('Electricity generated by the system per year')}
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.annualGeneration?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <TreeViewModal
        initExpandLevel={0}
        tree={transformedTree}
        closeModal={() => setShowEntitySelector(false)}
        show={showEntitySelector}
        title={t('Select Entity')}
      />
    </>
  );
};

export default RooftopSolarForm;
